import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  RESPONSE_BUTTON_MODES,
  RESPONSE_BUTTON_SURFACES,
  getResponseButtonSurfaceRolloutHint,
  type ResponseButtonFallbackMessageMapping,
  type ResponseButtonConfig,
  type ResponseButtonSurfaceConfig,
  type ResponseButtonSurfaceKey,
} from '../lib/responseButtons';

interface ResponseButtonConfigEditorProps {
  value: ResponseButtonConfig;
  onChange: (value: ResponseButtonConfig) => void;
  error?: string | null;
}

const WHATSAPP_MAX_BUTTONS = 3;

function createEmptyButton() {
  return { id: '', label: '' };
}

function sanitizeMaxButtonsInput(value: string): number | null {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  if (!/^[+-]?\d+$/.test(trimmedValue)) {
    return null;
  }

  const parsedValue = Number.parseInt(trimmedValue, 10);
  return Math.max(1, Math.min(WHATSAPP_MAX_BUTTONS, parsedValue));
}

function getSurfaceButtonLimit(surface: ResponseButtonSurfaceConfig): number {
  return Math.max(1, Math.min(WHATSAPP_MAX_BUTTONS, surface.maxButtons ?? WHATSAPP_MAX_BUTTONS));
}

function getNormalizedButtonId(button: { id: string }): string {
  return button.id.trim();
}

function syncFallbackMessageMappingForButtons(
  currentMapping: ResponseButtonFallbackMessageMapping | undefined,
  previousButtons: Array<{ id: string }> | undefined,
  nextButtons: Array<{ id: string }>,
): ResponseButtonFallbackMessageMapping | undefined {
  if (!currentMapping) {
    return undefined;
  }

  const previousMapping = currentMapping;

  const nextEntries = nextButtons.flatMap((button, index) => {
    const nextId = getNormalizedButtonId(button);
    const previousId = previousButtons?.[index] ? getNormalizedButtonId(previousButtons[index]) : '';

    if (!nextId) {
      return [];
    }

    if (nextId in previousMapping) {
      return [[nextId, previousMapping[nextId] ?? ''] as const];
    }

    if (previousId && previousId in previousMapping) {
      return [[nextId, previousMapping[previousId] ?? ''] as const];
    }

    return [[nextId, ''] as const];
  });

  if (nextEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(nextEntries);
}

export function ResponseButtonConfigEditor({
  value,
  onChange,
  error,
}: ResponseButtonConfigEditorProps) {
  const [maxButtonInputs, setMaxButtonInputs] = useState<Record<ResponseButtonSurfaceKey, string | undefined>>({
    dailyCheckIn: undefined,
    weeklySummary: undefined,
    chatResponses: undefined,
  });
  const buttonRowIdsRef = useRef<Record<ResponseButtonSurfaceKey, string[]>>({
    dailyCheckIn: [],
    weeklySummary: [],
    chatResponses: [],
  });
  const nextButtonRowIdRef = useRef(0);

  useEffect(() => {
    for (const surfaceMeta of RESPONSE_BUTTON_SURFACES) {
      const buttonCount = value[surfaceMeta.key]?.staticButtons?.length ?? 0;
      const rowIds = buttonRowIdsRef.current[surfaceMeta.key];

      while (rowIds.length < buttonCount) {
        rowIds.push(`${surfaceMeta.key}-button-${nextButtonRowIdRef.current++}`);
      }

      if (rowIds.length > buttonCount) {
        rowIds.splice(buttonCount);
      }
    }
  }, [
    value.dailyCheckIn?.staticButtons?.length,
    value.weeklySummary?.staticButtons?.length,
    value.chatResponses?.staticButtons?.length,
  ]);

  useEffect(() => {
    setMaxButtonInputs({
      dailyCheckIn: String(getSurfaceButtonLimit(value.dailyCheckIn ?? { enabled: false, mode: 'static' })),
      weeklySummary: String(getSurfaceButtonLimit(value.weeklySummary ?? { enabled: false, mode: 'static' })),
      chatResponses: String(getSurfaceButtonLimit(value.chatResponses ?? { enabled: false, mode: 'static' })),
    });
  }, [
    value.dailyCheckIn?.maxButtons,
    value.weeklySummary?.maxButtons,
    value.chatResponses?.maxButtons,
  ]);

  const appendButtonRowId = (surfaceKey: ResponseButtonSurfaceKey) => {
    buttonRowIdsRef.current[surfaceKey].push(`${surfaceKey}-button-${nextButtonRowIdRef.current++}`);
  };

  const removeButtonRowId = (surfaceKey: ResponseButtonSurfaceKey, index: number) => {
    buttonRowIdsRef.current[surfaceKey].splice(index, 1);
  };

  const updateSurface = (
    surfaceKey: ResponseButtonSurfaceKey,
    updater: (surface: ResponseButtonSurfaceConfig) => ResponseButtonSurfaceConfig
  ) => {
    const currentSurface = value[surfaceKey];
    if (!currentSurface) {
      return;
    }

    onChange({
      ...value,
      [surfaceKey]: updater(currentSurface),
    });
  };

  const commitMaxButtonsValue = (
    surfaceKey: ResponseButtonSurfaceKey,
    inputValue: string,
    fallbackValue: number,
  ) => {
    const sanitizedValue = sanitizeMaxButtonsInput(inputValue);

    if (sanitizedValue == null) {
      setMaxButtonInputs((current) => ({
        ...current,
        [surfaceKey]: String(fallbackValue),
      }));
      return;
    }

    setMaxButtonInputs((current) => ({
      ...current,
      [surfaceKey]: String(sanitizedValue),
    }));

    updateSurface(surfaceKey, (current) => {
      const previousButtons = current.staticButtons ?? [];
      const nextButtons = previousButtons.slice(0, sanitizedValue);
      return {
        ...current,
        maxButtons: sanitizedValue,
        ...(previousButtons.length > sanitizedValue ? { staticButtons: nextButtons } : {}),
        fallbackMessageMapping: syncFallbackMessageMappingForButtons(
          current.fallbackMessageMapping,
          previousButtons,
          nextButtons,
        ),
      };
    });
  };
  const hasEnabledSurface = RESPONSE_BUTTON_SURFACES.some((surfaceMeta) => value[surfaceMeta.key]?.enabled);

  return (
    <div style={SURFACE_STYLES.wrapper}>
      <div style={SURFACE_STYLES.headerRow}>
        <label style={SURFACE_STYLES.checkboxLabel}>
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          />
          <span style={SURFACE_STYLES.checkboxText}>Enable response buttons</span>
        </label>
        <span style={SURFACE_STYLES.headerHint}>WhatsApp supports up to 3 short reply buttons.</span>
      </div>

      {value.enabled && !hasEnabledSurface && (
        <div style={SURFACE_STYLES.inlineWarning}>
          Enable at least 1 surface before saving while response buttons are enabled.
        </div>
      )}

      {RESPONSE_BUTTON_SURFACES.map((surfaceMeta) => {
        const surface = value[surfaceMeta.key];
        if (!surface) {
          return null;
        }

        const buttonCount = surface.staticButtons?.length ?? 0;
        const buttonRowIds = buttonRowIdsRef.current[surfaceMeta.key];
        const showStaticButtons = surface.mode === 'static' || surface.mode === 'hybrid';
        const showAiPrompt = surface.mode === 'ai' || surface.mode === 'hybrid';
        const needsStaticButton = surface.enabled && showStaticButtons && buttonCount === 0;
        const surfaceButtonLimit = getSurfaceButtonLimit(surface);
        const maxButtonsInputValue = maxButtonInputs[surfaceMeta.key] ?? String(surfaceButtonLimit);

        return (
          <div key={surfaceMeta.key} style={SURFACE_STYLES.surfaceCard}>
            <div style={SURFACE_STYLES.surfaceHeader}>
              <div>
                <div style={SURFACE_STYLES.surfaceTitle}>{surfaceMeta.label}</div>
                <div style={SURFACE_STYLES.surfaceDescription}>{surfaceMeta.description}</div>
              </div>
              <label style={SURFACE_STYLES.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={surface.enabled ?? false}
                  onChange={(e) => updateSurface(surfaceMeta.key, (current) => ({
                    ...current,
                    enabled: e.target.checked,
                  }))}
                />
                <span style={SURFACE_STYLES.checkboxText}>Enabled</span>
              </label>
            </div>

            <div style={SURFACE_STYLES.grid}>
              <div>
                <label style={SURFACE_STYLES.label}>Mode</label>
                <select
                  value={surface.mode}
                  onChange={(e) => updateSurface(surfaceMeta.key, (current) => ({
                    ...current,
                    mode: e.target.value as ResponseButtonSurfaceConfig['mode'],
                  }))}
                  style={SURFACE_STYLES.input}
                >
                  {RESPONSE_BUTTON_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  style={SURFACE_STYLES.label}
                  htmlFor={`${surfaceMeta.key}-max-buttons`}
                >
                  Max buttons
                </label>
                <input
                  id={`${surfaceMeta.key}-max-buttons`}
                  type="number"
                  min={1}
                  max={3}
                  value={maxButtonsInputValue}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setMaxButtonInputs((current) => ({
                      ...current,
                      [surfaceMeta.key]: nextValue,
                    }));
                  }}
                  onBlur={() => commitMaxButtonsValue(surfaceMeta.key, maxButtonsInputValue, surface.maxButtons ?? 3)}
                  style={SURFACE_STYLES.input}
                />
                <div style={SURFACE_STYLES.inlineHint}>1 to 3 buttons. Static buttons are capped by this value.</div>
              </div>
            </div>

            <div style={SURFACE_STYLES.inlineHint}>
              {getResponseButtonSurfaceRolloutHint(surfaceMeta.key)}
            </div>

            {!surface.enabled && (
              <div style={SURFACE_STYLES.inlineHint}>
                Disabled surfaces keep their draft buttons and prompt guidance. Runtime should ignore them until re-enabled.
              </div>
            )}

            {showStaticButtons && (
              <div style={SURFACE_STYLES.staticButtonsSection}>
                <div style={SURFACE_STYLES.sectionHeader}>
                  <span style={SURFACE_STYLES.sectionTitle}>Static buttons</span>
                  <button
                    type="button"
                    onClick={() => {
                      appendButtonRowId(surfaceMeta.key);
                      updateSurface(surfaceMeta.key, (current) => ({
                        ...current,
                        staticButtons: [...(current.staticButtons ?? []), createEmptyButton()].slice(0, getSurfaceButtonLimit(current)),
                        fallbackMessageMapping: syncFallbackMessageMappingForButtons(
                          current.fallbackMessageMapping,
                          current.staticButtons,
                          [...(current.staticButtons ?? []), createEmptyButton()].slice(0, getSurfaceButtonLimit(current)),
                        ),
                      }));
                    }}
                    disabled={buttonCount >= surfaceButtonLimit}
                    style={{
                      ...SURFACE_STYLES.addButton,
                      ...(buttonCount >= surfaceButtonLimit ? SURFACE_STYLES.disabledButton : {}),
                    }}
                  >
                    Add button
                  </button>
                </div>
                <div style={SURFACE_STYLES.inlineHint}>
                  Up to {surfaceButtonLimit} button{surfaceButtonLimit === 1 ? '' : 's'} on this surface. Label max: 20 characters.
                </div>
                {needsStaticButton && (
                  <div style={SURFACE_STYLES.inlineWarning}>
                    Add at least 1 static button before saving an enabled {surface.mode} surface.
                  </div>
                )}

                {(surface.staticButtons ?? []).length === 0 && (
                  <div style={SURFACE_STYLES.emptyState}>No static buttons configured.</div>
                )}

                {(surface.staticButtons ?? []).map((button, index) => (
                  <div key={buttonRowIds[index] ?? `${surfaceMeta.key}-button-${index}`} style={SURFACE_STYLES.buttonRow}>
                    <div style={SURFACE_STYLES.buttonGrid}>
                      <div>
                        <label style={SURFACE_STYLES.label}>Button label</label>
                        <input
                          type="text"
                          value={button.label}
                          maxLength={20}
                          onChange={(e) => updateSurface(surfaceMeta.key, (current) => {
                            const nextButtons = [...(current.staticButtons ?? [])];
                            nextButtons[index] = { ...nextButtons[index], label: e.target.value };
                            return { ...current, staticButtons: nextButtons };
                          })}
                          style={SURFACE_STYLES.input}
                          placeholder="e.g. Yes"
                        />
                        <div style={SURFACE_STYLES.inlineHint}>{button.label.length}/20</div>
                      </div>

                      <div>
                        <label style={SURFACE_STYLES.label}>Button id</label>
                        <input
                          type="text"
                          value={button.id}
                          maxLength={256}
                          onChange={(e) => updateSurface(surfaceMeta.key, (current) => {
                            const previousButtons = current.staticButtons ?? [];
                            const nextButtons = [...(current.staticButtons ?? [])];
                            nextButtons[index] = { ...nextButtons[index], id: e.target.value };
                            return {
                              ...current,
                              staticButtons: nextButtons,
                              fallbackMessageMapping: syncFallbackMessageMappingForButtons(
                                current.fallbackMessageMapping,
                                previousButtons,
                                nextButtons,
                              ),
                            };
                          })}
                          style={SURFACE_STYLES.input}
                          placeholder="internal-id"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        removeButtonRowId(surfaceMeta.key, index);
                        updateSurface(surfaceMeta.key, (current) => {
                          const previousButtons = current.staticButtons ?? [];
                          const nextButtons = previousButtons.filter((_, buttonIndex) => buttonIndex !== index);

                          return {
                            ...current,
                            staticButtons: nextButtons,
                            fallbackMessageMapping: syncFallbackMessageMappingForButtons(
                              current.fallbackMessageMapping,
                              previousButtons,
                              nextButtons,
                            ),
                          };
                        });
                      }}
                      style={SURFACE_STYLES.removeButton}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showStaticButtons && buttonCount > 0 && (
              <div style={SURFACE_STYLES.fallbackSection}>
                <div style={SURFACE_STYLES.sectionHeader}>
                  <span style={SURFACE_STYLES.sectionTitle}>Fallback messages</span>
                </div>
                <div style={SURFACE_STYLES.inlineHint}>
                  Optional per-button text to send if WhatsApp cannot deliver a reply button. When any fallback is set, all current button IDs are saved together.
                </div>

                {(surface.staticButtons ?? []).map((button, index) => (
                  <div
                    key={`${buttonRowIds[index] ?? `${surfaceMeta.key}-button-${index}`}-fallback`}
                    style={SURFACE_STYLES.fallbackRow}
                  >
                    <label style={SURFACE_STYLES.label}>
                      Fallback for <code>{button.id || `button ${index + 1}`}</code>
                    </label>
                    <textarea
                      value={getNormalizedButtonId(button)
                        ? (surface.fallbackMessageMapping?.[getNormalizedButtonId(button)] ?? '')
                        : ''}
                      onChange={(e) => updateSurface(surfaceMeta.key, (current) => {
                        const buttonId = getNormalizedButtonId(current.staticButtons?.[index] ?? button);

                        if (!buttonId) {
                          return current;
                        }

                        return {
                          ...current,
                          fallbackMessageMapping: {
                            ...(current.fallbackMessageMapping ?? {}),
                            [buttonId]: e.target.value,
                          },
                        };
                      })}
                      style={SURFACE_STYLES.textarea}
                      rows={2}
                      disabled={!getNormalizedButtonId(button)}
                      placeholder="Optional fallback text if this button cannot be delivered."
                    />
                    {!getNormalizedButtonId(button) && (
                      <div style={SURFACE_STYLES.inlineHint}>
                        Enter a button ID before adding a fallback message.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div>
              <label style={SURFACE_STYLES.label}>
                AI prompt guidance {showAiPrompt ? '' : '(optional)'}
              </label>
              <textarea
                value={surface.aiPromptOverride ?? ''}
                onChange={(e) => updateSurface(surfaceMeta.key, (current) => ({
                  ...current,
                  aiPromptOverride: e.target.value,
                }))}
                style={SURFACE_STYLES.textarea}
                rows={3}
                placeholder={surfaceMeta.key === 'chatResponses'
                  ? 'Guide AI-generated chat reply buttons.'
                  : 'Optional guidance for AI-generated buttons.'}
              />
              <div style={SURFACE_STYLES.inlineHint}>
                Used for AI and hybrid modes. Keep guidance narrow so the model proposes buttons sparingly instead of attaching them to every reply.
              </div>
            </div>
          </div>
        );
      })}

      {error ? <div style={SURFACE_STYLES.error}>{error}</div> : null}
    </div>
  );
}

const SURFACE_STYLES: Record<string, CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  checkboxLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#1f2937',
    fontWeight: 500,
  },
  checkboxText: {
    lineHeight: 1.4,
  },
  headerHint: {
    fontSize: '12px',
    color: '#6b7280',
  },
  surfaceCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    backgroundColor: '#fff',
  },
  surfaceHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  surfaceTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '4px',
  },
  surfaceDescription: {
    fontSize: '12px',
    color: '#64748b',
    lineHeight: 1.5,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#475569',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
    resize: 'vertical',
    backgroundColor: '#fff',
  },
  inlineHint: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#64748b',
    lineHeight: 1.5,
  },
  inlineWarning: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#b45309',
    lineHeight: 1.5,
  },
  staticButtonsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#0f172a',
  },
  addButton: {
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#0f172a',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  disabledButton: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  emptyState: {
    fontSize: '13px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  buttonRow: {
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: '#f8fafc',
  },
  buttonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
  },
  fallbackSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  fallbackRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  removeButton: {
    alignSelf: 'flex-start',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#dc2626',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
  },
  error: {
    color: '#b91c1c',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '10px',
    padding: '12px 14px',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
  },
};
